import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styles from './FeedbackPage.module.css';
import pineappleLogo from '../images/core/pineapple-logo-black-text.svg';
import LeftChevron from '../components/icons/LeftChevron';
import CrossIcon from '../components/icons/CrossIcon';
import { FeedbackNextSteps, getFeedbackForm, submitFeedbackForm, saveFeedbackPartial } from '../redux/feedbackForm/actions/feedbackForm.action';
import Loader from '../components/common/loader/Loader';
import Toast from '../components/toast/Toast';


type Step = 1 | 2 | 3;

type RatingOption = {
  rating: number;
  label: string;
  emoji: string;
};

const ratingOptions: RatingOption[] = [
  { rating: 1, label: 'Bad', emoji: '😠' },
  { rating: 2, label: 'Poor', emoji: '😕' },
  { rating: 3, label: 'OK', emoji: '😐' },
  { rating: 4, label: 'Good', emoji: '🙂' },
  { rating: 5, label: 'Great', emoji: '🤩' },
];

const reasonsByRating: Record<number, string[]> = {
  1: ['Unit condition', "Couldn't get in", 'Wrong unit', 'Property looked off', 'Neighborhood', 'Pricing', 'No one responded'],
  2: ['Unit condition', "Couldn't get in", 'Wrong unit', 'Property looked off', 'Neighborhood', 'Pricing', 'No one responded'],
  3: ['Cleaner unit', 'More info upfront', 'Easier check-in', 'Better signage', 'Different unit', 'Lower price'],
  4: ['Unit condition', 'Easy check-in', 'Property vibe', 'Neighborhood', 'Amenities', 'Met the team', 'Pricing', 'Floor plan'],
  5: ['Unit condition', 'Easy check-in', 'Property vibe', 'Neighborhood', 'Amenities', 'Met the team', 'Pricing', 'Floor plan'],
};

const reasonByRatingTitle: Record<number, React.ReactNode> = {
  1: <>Sorry about that. What <span className={styles.headingItalic}>went wrong</span>?</>,
  2: <>Sorry about that. What <span className={styles.headingItalic}>went wrong</span>?</>,
  3: <>What would've <span className={styles.headingItalic}>made it better</span>?</>,
  4: <>Love to hear it — what <span className={styles.headingItalic}>stood out</span>?</>,
  5: <>Love to hear it — what <span className={styles.headingItalic}>stood out</span>?</>,
};


const defaultCtaByRating = (selectedRating: number | null, unitNumber: string) => {
  if (!selectedRating || selectedRating === 3) {
    return {
      title: 'Thanks',
      primary: { label: `Apply for ${unitNumber || 'this unit'}`, type: 'LINK', action: '/apply' },
      secondary: { label: 'Browse other units', type: 'LINK', action: '/appointments' },
    };
  }

  if (selectedRating <= 2) {
    return {
      title: 'Thanks',
      primary: { label: 'Let us make it right', type: 'SMS', action: 'sms:?body=Hi Maria, I just filled out feedback from my tour and need help.' },
    };
  }

  return {
    title: 'Thanks',
    primary: { label: `Apply for ${unitNumber || 'this unit'}`, type: 'LINK', action: '/apply' },
    secondary: { label: 'Browse other units', type: 'LINK', action: '/appointments' },
  };
};

const formatTourDate = (tourDate?: string) => {
  if (!tourDate) return '';
  const parsedDate = new Date(tourDate);
  if (Number.isNaN(parsedDate.getTime())) return tourDate;
  return parsedDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const initialsFromText = (text?: string) => {
  if (!text) return 'FB';
  const letters = text
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('');
  return letters || 'FB';
};

const isValidFeedbackToken = (value: string) => {
  if (!value || value.startsWith(':')) return false;
  return /^[A-Za-z0-9_-]{16,}$/.test(value);
};

const FeedbackPage = () => {
  const routeParams = useParams();
  const token = useMemo(() => {
    const rawToken = routeParams.token || routeParams['1'] || '';
    return typeof rawToken === 'string' ? rawToken.trim() : '';
  }, [routeParams]);

  const [prospectName, setProspectName] = useState('');

  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [rating, setRating] = useState<number | null>(null);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [animatingRating, setAnimatingRating] = useState<number | null>(null);
  const advanceTimerRef = useRef<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [propertyName, setPropertyName] = useState('null');
  const [unitNumber, setUnitNumber] = useState('null');
  const [tourDate, setTourDate] = useState('null');
  // const [isNoShow, setIsNoShow] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [feedbackType, setFeedBackType] = useState<string | null>(null);
  const [nextSteps, setNextSteps] = useState<FeedbackNextSteps | null>(null);

  const [toast, setToast] = useState<{ show: boolean; type: 'success' | 'error' | 'warning'; message: string }>({
    show: false,
    type: 'success',
    message: '',
  });

  const showToastMessage = (type: 'success' | 'error' | 'warning', message: string) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast((prev) => ({ ...prev, show: false })), 4000);
  };

  useEffect(() => {
    if (!toast.show) return;

    const timer = setTimeout(() => {
      setToast((prev) => ({ ...prev, show: false }));
    }, 4000);

    return () => clearTimeout(timer);
  }, [toast.show]);


  const progressPct = step === 1 ? 33 : step === 2 ? 66 : 100;
  const selectedRating = useMemo(() => ratingOptions.find((r) => r.rating === rating) || null, [rating]);
  const reasonOptions = useMemo(() => {
    if (!rating) return [];
    return reasonsByRating[rating] || [];
  }, [rating]);

  const cta = useMemo(() => {
    const fallback = defaultCtaByRating(rating, unitNumber);
    return {
      title: 'Thanks',
      subtitle: 'Your feedback is on its way to the team.',
      primary: nextSteps?.primary || fallback.primary,
      secondary: nextSteps?.secondary || fallback.secondary,
    };
  }, [nextSteps, rating, unitNumber]);

  useEffect(() => {
    let mounted = true;
    const initializeFeedbackForm = async () => {
      if (!isValidFeedbackToken(token)) {
        setErrorMessage('Invalid feedback link.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await getFeedbackForm(token);
        if (!mounted) return;

        if (response.status === 'ALREADY_SUBMITTED') {
          setStep(3);
          setNextSteps(response.nextSteps || null);
          setProspectName(`${response?.data?.firstName || ''} ${response?.data?.lastName || ''}`);
          setFeedBackType(response?.type || null);
          setIsLoading(false);
          return;
        }

        if (response.status !== 'ACTIVE') {
          setIsExpired(true);
          setIsLoading(false);
          return;
        }

        setPropertyName(response.propertyName || '');
        setUnitNumber(response.unitNumber ? `Unit ${response.unitNumber}` : 'Unit');
        setTourDate(formatTourDate(response.tourDate) || 'Tour date');

        // Client-side expiry check: 30 days after tour
        if (response.tourDate) {
          const tourDate = new Date(response.tourDate);
          const now = new Date();
          const diffInDays = (now.getTime() - tourDate.getTime()) / (1000 * 60 * 60 * 24);

          if (diffInDays > 30) {
            setIsExpired(true);
            setIsLoading(false);
            return;
          }
        }

        // setIsNoShow(Boolean(response.isNoShow));
        setRating(response.rating ?? null);
        setFeedBackType(response?.type || null);
        setStep(response.rating ? 2 : 1);
      } catch (error) {
        if (!mounted) return;

        setErrorMessage('Unable to load feedback form. Please try again.');
        showToastMessage('error', 'Unable to load feedback form.');
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    initializeFeedbackForm();
    return () => {
      mounted = false;
      if (advanceTimerRef.current) window.clearTimeout(advanceTimerRef.current);
    };
  }, [token]);

  const submitFeedback = async () => {
    if (!token) return;
    if (rating === null && !isExpired) return;
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const response = await submitFeedbackForm(token, {
        rating,
        reasons: selectedReasons,
        freeText: notes,
      });
      setNextSteps(response?.nextSteps || null);
      setProspectName(`${response?.data?.firstName || ''} ${response?.data?.lastName || ''}`);

      setStep(3);
      showToastMessage('success', 'Feedback submitted successfully!');
      if (response?.type) {
        setFeedBackType(response?.type);
      }
    } catch (error) {
      setErrorMessage('Unable to submit feedback. Please try again.');
      showToastMessage('error', 'Unable to submit feedback.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSelectRating = (nextRating: number) => {
    setAnimatingRating(nextRating);
    setRating(nextRating);
    setSelectedReasons([]);
    setErrorMessage('');

    if (isValidFeedbackToken(token)) {
      saveFeedbackPartial(token, { rating: nextRating }).catch(() => {
        setErrorMessage('Rating saved locally. We will retry when you submit.');
        showToastMessage('warning', 'Rating saved locally.');
      });
    }

    if (advanceTimerRef.current) window.clearTimeout(advanceTimerRef.current);
    advanceTimerRef.current = window.setTimeout(() => {
      setAnimatingRating(null);
      setStep(2);
    }, 150);
  };

  const toggleReason = (reason: string) => {
    setSelectedReasons((current) => (current.includes(reason) ? current.filter((item) => item !== reason) : [...current, reason]));
  };

  const onSkip = async () => {
    if (step === 1) {
      setStep(2);
      return;
    }
    if (step === 2) {
      if (rating !== null) {
        await submitFeedback();
      } else {
        setStep(3);
      }
    }
  };

  const executeCta = (action?: string, type?: string) => {
    if (!action) return;
    if (type === 'SMS' || action.startsWith('sms:')) {
      window.location.href = action;
      return;
    }
    if (action.startsWith('http')) {
      window.location.href = action;
      return;
    }
    navigate(action);
  };

  const shouldShowCounter = notes.length >= 400;

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={`${styles.card} ${styles.cardWrap} `}>
          <div className={styles.brand}>
            <img src={pineappleLogo} alt="Pineapple" className={styles.brandLogo} />
          </div>
          {/* <p className={styles.subheading}>Loading feedback...</p> */}
          <Loader />
        </div>
      </div>
    );
  }

  if (errorMessage && isExpired) {
    return (
      <div className={styles.page}>
        <div className={`${styles.card} ${styles.cardWrap} `}>
          <div className={styles.brand}>
            <Link to="/">
              <img src={pineappleLogo} alt="Pineapple" className={styles.brandLogo} />
            </Link>
          </div>
          <p className={styles.subheading}>{errorMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {!isExpired || step === 3 ? (
        <div className={styles.card}>
          <div className={styles.topbar}>
            {step !== 3 ? (
              <button className={styles.linkButton} onClick={() => (step === 2 ? setStep(1) : navigate('/'))} aria-label="Close">
                {step === 2 ? <LeftChevron width={20} height={20} /> : <CrossIcon size={20} />}
              </button>
            ) : (
              <span className={styles.placeholder} />
            )}
            <div className={styles.brand}>
              <Link to="/">
                <img src={pineappleLogo} alt="Pineapple" className={styles.brandLogo} />
              </Link>
            </div>
            {step === 2 ? (
              <button className={styles.linkButton} onClick={onSkip}>
                Skip
              </button>
            ) : (
              <span className={styles.placeholder} />
            )}
          </div>

          <div className={styles.progress}>
            <div className={[styles.progressFill, step === 3 ? styles.progressFillDone : ''].join(' ')} style={{ width: `${progressPct}% ` }} />
          </div>

          {step === 1 ? (
            <>
              <div className={styles.firstStepContent}>
                <div className={styles.propertyChip}>
                  <span className={styles.propertyAvatar}>{initialsFromText(propertyName)}</span>
                  <span>
                    <strong>{propertyName}</strong> · {unitNumber} · {tourDate}
                  </span>
                </div>

                <h1 className={styles.heading}>
                  How was your <span className={styles.headingItalic}>tour</span>?
                </h1>
                <p className={styles.subheading}>
                  One question. We want it straight - even if it's critical.
                </p>

                <div className={styles.ratingList}>
                  {ratingOptions.map((option) => {
                    const active = rating === option.rating;
                    const animating = animatingRating === option.rating;
                    return (
                      <button
                        key={option.rating}
                        className={[styles.ratingBtn, active ? styles.ratingBtnActive : '', animating ? styles.ratingBtnAnimating : ''].join(' ')}
                        onClick={() => onSelectRating(option.rating)}
                      >
                        <span className={styles.ratingLeft}>
                          <span className={styles.ratingFace}>{option.emoji}</span>
                          <span className={styles.ratingLabel}>{option.label}</span>
                        </span>
                        <span className={styles.ratingScore}>{option.rating} / 5</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          ) : null}

          {step === 2 ? (
            <>
              <div className={styles.stepTwoContentWrap}>
                <div className={styles.stepTwoContent}>
                  <div className={styles.stepTwoMeta}>
                    <span className={styles.selectedBadge}>
                      {selectedRating?.emoji} {selectedRating?.label}
                    </span>
                    <span className={styles.ratingMeta}>· You rated it {rating}/5</span>
                  </div>
                  <h1 className={styles.heading}>
                    {rating ? reasonByRatingTitle[rating] : 'Tell us more about your experience'}
                  </h1>
                  <p className={styles.subheading}>Tap anything that applies. Multi-select.</p>

                  <div className={styles.pillGrid}>
                    {reasonOptions.map((reason) => (
                      <button
                        key={reason}
                        className={[styles.pill, selectedReasons.includes(reason) ? styles.pillActive : ''].join(' ')}
                        onClick={() => toggleReason(reason)}
                      >
                        {reason}
                      </button>
                    ))}
                  </div>

                  <div className={styles.textAreaWrap}>
                    <label className={styles.textAreaLabel} htmlFor="feedback-notes">
                      Anything else? (optional)
                    </label>
                    <textarea
                      id="feedback-notes"
                      className={styles.textarea}
                      value={notes}
                      onChange={(event) => setNotes(event.target.value.slice(0, 500))}
                      placeholder="The unit was cleaner than most I've seen — that mattered a lot."
                      maxLength={500}
                    />
                    {shouldShowCounter ? <div className={styles.counter}>{500 - notes.length} characters left</div> : null}
                  </div>
                </div>

                <button className={styles.primaryBtn} onClick={submitFeedback} disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Continue →'}
                </button>
              </div>
            </>
          ) : null}

          {step === 3 ? (
            <div className={styles.stepTwoContentWrap}>
              <div className={styles.thankYouBody}>
                <div className={styles.thankYouMark}>🙏</div>
                <h1 className={styles.heading}>{cta.title}, <span className={styles.headingItalic}>{prospectName}</span>.</h1>
                <p className={styles.subheading}>Your feedback’s on its way to the {prospectName} 'team' — they read every one.</p>
                <>{feedbackType !== "EXPIRED_FEEDBACK" && <>
                  <div className={styles.nextStepsLabel}>What&apos;s next?</div>
                  <button className={styles.primaryBtn + ' ' + styles.nextStepBtn} onClick={() => executeCta(cta.primary.action, cta.primary.type)}>
                    <div className={styles.nextStepContainer}>
                      <div className={styles.nextStepIcon}>📝</div>
                      <div className={styles.nextStepText}>{cta.primary.label}</div>
                    </div>
                    <div className={styles.nextStepArrow}>→</div>
                  </button>
                  {cta.secondary && (
                    <button className={styles.secondaryBtn + ' ' + styles.nextStepBtn} onClick={() => executeCta(cta.secondary?.action, cta.secondary?.type)}>
                      <div className={styles.nextStepContainer}>
                        <div className={styles.nextStepIcon}>🏠</div>
                        <div className={styles.nextStepText}>{cta.secondary.label}</div>
                      </div>
                      <div className={styles.nextStepArrow}>→</div>
                    </button>
                  )}
                </>}
                </>
                <button className={styles.mutedCenterLink} onClick={() => navigate('/')}>
                  All done
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div className={styles.card + ' ' + styles.cardWrap}>
          <div className={styles.brand}>
            <img src={pineappleLogo} alt="Pineapple" className={styles.brandLogo} />
          </div>
          <div className={styles.progress}>

          </div>
          <div className={styles.stepTwoContentWrap}>
            <div className={styles.stepTwoContent}>
              <h1 className={styles.heading}>
                This link has expired, but we'd still love to hear from you
              </h1>


              <div className={styles.textAreaWrap}>
                <label className={styles.textAreaLabel} htmlFor="feedback-notes">
                  Anything else? (optional)
                </label>
                <textarea
                  id="feedback-notes"
                  className={styles.textarea}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value.slice(0, 500))}
                  placeholder="The unit was cleaner than most I've seen — that mattered a lot."
                  maxLength={500}
                />
                {shouldShowCounter ? <div className={styles.counter}>{500 - notes.length} characters left</div> : null}
              </div>
            </div>

            <button className={styles.primaryBtn} onClick={submitFeedback} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Continue →'}
            </button>
          </div>
        </div>
      )}
      {toast.show && <Toast type={toast.type} message={toast.message} />}
    </div>
  );
};

export default FeedbackPage;
